import {
	hasHistorySupport,
	updateURLParameter,
	getTimestamp,
	remove,
} from '../utilities/utilities';
import { loadingStart, loadingEnd } from '../loader/loader';
import pubsub from '../pubsub/pubsub';
import axios from 'axios';

/**
 * Load more
 * A plugin that implements a "load more" pagination, with url updates and back-button support.
 * @see {@link http://tumbledry.org/2011/05/12/screw_hashbangs_building}
 * @version 3.0.0
 * @exports LoadMore
 */
export default class LoadMore {
	/**
	 * Constructor
	 * @param {Object} el - A DOM node.
	 * @public
	 */
	constructor(el) {
		/** Placeholder for base DOM element. */
		this.el = null;

		/** Placeholder for template (used if this plugin is used with json). */
		this.template = null;

		/** Target DOM id (the list). */
		this.targetId = null;

		/** Placeholder for target DOM element (the list). */
		this.targetEl = null;

		/** Boolean to indicate if there are any ajax-request in progress. */
		this.requestInProgress = false;

		/** 1 = DOESN'T WORK CORRECTLY RIGHT NOW. back button navigates WITHIN the page. 0 = back button navigates to previous, referring page. */
		this.supportBackButton = 0;

		/** Placeholder to contain the starting page (so url can display page=1-3). */
		this.urlCarryPageBase = null;

		/** Array that will contain the loaded pages. */
		this.itemCount = [];

		/** Selector for items. */
		this.itemSelector = 'article';

		/** Enables infinity scroll instead of visible load-more button **/
		this.infinity = false;

		/** The loader container element when using infinity scroll **/
		this.loaderEl = null;

		this._init(el);
	}

	/**
	 * The initialization function for this module.
	 * @param {Object} el - A DOM node.
	 * @public
	 */
	_init(el) {
		let that = this;

		if (!hasHistorySupport()) {
			return false;
		}

		this.el =
			el !== undefined ? el : document.querySelector('[data-type="loadMore"]');

		if (this.el === null) {
			return false;
		}

		this.infinity = this.el.getAttribute('data-infinity') === 'true';

		if (this.el.getAttribute('data-loader') !== null) {
			this.loaderEl = document.getElementById(
				this.el.getAttribute('data-loader')
			);
		}

		this.targetId = this.el.getAttribute('aria-controls');
		this.targetEl = document.getElementById(this.targetId);

		if (this.targetEl === null) {
			return false;
		}

		if (this.el.getAttribute('data-itemselector') !== null) {
			this.itemSelector = this.el.getAttribute('data-itemselector');
		}

		if (this.el.getAttribute('data-template') !== null) {
			require([
				'../../templates/' + that.el.getAttribute('data-template') + '.hbs',
			], tpl => {
				this.template = tpl;
				this._initEvents();
			});
		} else {
			this._initEvents();
		}
	}

	/**
	 * Function that loads next page through ajax.
	 * @private
	 */
	_load() {
		let url = this.el.getAttribute('href'),
			loadingMessage = this.el.getAttribute('data-loading-message') || 'Laddar';

		if (this.requestInProgress === false && this._pathchange(url)) {
			this.requestInProgress = true;
			loadingStart(this.loaderEl || this.el, loadingMessage, '', true);

			axios({
				url: updateURLParameter(url, '_', getTimestamp()),
				method: 'get',
				headers: {
					'x-requested-with': 'XMLHttpRequest',
				},
			})
				.then(response => {
					if (response.status === 200) {
						let ct = response.headers['content-type'] || '';

						if (ct.indexOf('html') > -1) {
							this._renderHtml(response.data);
						} else if (ct.indexOf('json') > -1) {
							this._renderJson(response.data);
						}

						this.requestInProgress = false;
						loadingEnd(this.loaderEl || this.el);
					}
				})
				.catch(() => {});
		}
	}

	/**
	 * Function that renders new html-data.
	 * @param {String} data - New data as a string of html.
	 * @private
	 */
	_renderHtml(data) {
		let parser = new DOMParser(),
			doc = parser.parseFromString(data, 'text/html'),
			newPager = doc.querySelector('[data-type="loadMore"]'),
			newItems = doc.querySelectorAll(
				'#' + this.targetId + ' ' + this.itemSelector
			);

		for (let i = 0; i < newItems.length; i++) {
			this.targetEl.appendChild(newItems[i]);

			if (i === 0) {
				let firstLink = newItems[i].querySelector('a');

				if (firstLink !== null) {
					firstLink.focus();
				} else {
					newItems[i].focus();
				}
			}
		}

		this.itemCount.push(newItems.length);

		if (newPager === null) {
			remove(this.el);
		} else {
			this.el.setAttribute('href', newPager.getAttribute('href'));
		}
		pubsub.publish('/ajaxForm/list/updated');
		pubsub.publish('/ajaxForm/sync/update/html', doc);
	}

	/**
	 * Function that renders new json-data.
	 * @param {String} data - New data as a string of json.
	 * @private
	 */
	_renderJson(data) {
		let html = '',
			oldItems = null,
			newItems = null,
			firstLink;

		try {
			html = this.template(data);
		} catch (e) {}

		if (
			data.pager.pagerUrl === undefined ||
			data.pager.pagerUrl === null ||
			data.pager.pagerUrl === ''
		) {
			this.el.classList.add('btn--hidden');
		} else {
			this.el.classList.remove('btn--hidden');
			this.el.setAttribute('href', data.pager.pagerUrl);
		}

		oldItems = this.targetEl.querySelectorAll(this.itemSelector);
		this.targetEl.insertAdjacentHTML('BeforeEnd', html);
		newItems = this.targetEl.querySelectorAll(this.itemSelector);
		this.itemCount.push(newItems.length - oldItems.length);

		if (
			newItems[oldItems.length] !== null &&
			newItems[oldItems.length] !== undefined
		) {
			firstLink = newItems[oldItems.length].querySelector('a');
			if (firstLink !== null) {
				firstLink.focus();
			} else {
				newItems[oldItems.length].focus();
			}
		}
		pubsub.publish('/ajaxForm/list/updated');
		pubsub.publish('/ajaxForm/sync/update/json', data);
	}

	/**
	 * Function that updates the url.
	 * @private
	 */
	_pathchange(path) {
		let regexp = /\/pages?\/([0-9]+)-?[0-9]*\/?$/,
			locationHref = window.location.href,
			basePath = path,
			nextPage;

		// Query-based
		if (path.indexOf('?page=') !== -1 || path.indexOf('&page=') !== -1) {
			this.queryBased = true;
			regexp = /pages?=([0-9]+)-?[0-9]*\/?$/;
		} else if (path.indexOf('?') !== -1) {
			basePath = path.split('?')[0];
		}

		if (
			locationHref.indexOf('?') !== -1 &&
			(locationHref.indexOf('?page=') === -1 &&
				locationHref.indexOf('&page=') === -1)
		) {
			locationHref = locationHref.split('?')[0];
		}

		if (this.urlCarryPageBase === null) {
			if (regexp.test(locationHref)) {
				nextPage = regexp.exec(locationHref);
				this.urlCarryPageBase = nextPage[1];
			} else {
				nextPage = regexp.exec(basePath);
				this.urlCarryPageBase = nextPage[1] - 1;
			}
		}

		path = this.queryBased
			? path.replace('page=', 'pages=' + this.urlCarryPageBase + '-')
			: path.replace('page/', 'pages/' + this.urlCarryPageBase + '-');

		if (this.supportBackButton === 1) {
			window.history.pushState(
				null,
				null,
				updateURLParameter(path, '_', getTimestamp())
			);
		} else {
			window.history.replaceState(
				null,
				null,
				updateURLParameter(path, '_', getTimestamp())
			);
		}

		return true;
	}

	/**
	 * Gets the scroll XY coordinates.
	 * @private
	 * @see {@link http://jsfiddle.net/8LpFR/}
	 * @returns {Array} - The scroll XY coordinates.
	 */
	_getScrollCoordinates() {
		let scrollX = 0,
			scrollY = 0;

		if (typeof window.pageYOffset == 'number') {
			//Netscape compliant
			scrollY = window.pageYOffset;
			scrollX = window.pageXOffset;
		} else if (
			document.body &&
			(document.body.scrollLeft || document.body.scrollTop)
		) {
			//DOM compliant
			scrollY = document.body.scrollTop;
			scrollX = document.body.scrollLeft;
		} else if (
			document.documentElement &&
			(document.documentElement.scrollLeft ||
				document.documentElement.scrollTop)
		) {
			//IE6 standards compliant mode
			scrollY = document.documentElement.scrollTop;
			scrollX = document.documentElement.scrollLeft;
		}
		return [scrollX, scrollY];
	}

	/**
	 * Gets the document's height.
	 * @private
	 * @see {@link http://jsfiddle.net/8LpFR/}
	 * @returns {Number} - The document height.
	 */
	_getDocHeight() {
		let D = document;

		return Math.max(
			D.body.scrollHeight,
			D.documentElement.scrollHeight,
			D.body.offsetHeight,
			D.documentElement.offsetHeight,
			D.body.clientHeight,
			D.documentElement.clientHeight
		);
	}

	/**
	 * Initiates events.
	 * @private
	 */
	_initEvents() {
		let footerStartPosition =
			document.body.scrollHeight -
			document.querySelector('footer').scrollHeight;

		// For infinity scroll
		if (this.infinity) {
			window.addEventListener('scroll', () => {
				if (this.el.classList.contains('btn--hidden')) {
					return;
				}

				if (
					this._getDocHeight() - footerStartPosition / 5 <=
					this._getScrollCoordinates()[1] + window.innerHeight
				) {
					let lastDiv = this.targetEl.querySelector(
							this.itemSelector + ':last-child'
						),
						lastDivOffset = lastDiv.offsetTop + lastDiv.clientHeight,
						pageOffset = window.pageYOffset + window.innerHeight;

					if (pageOffset > lastDivOffset - 10) {
						this._load();
					}
				}
			});
			// For loadmore button
		} else {
			this.el.addEventListener('click', event => {
				event.preventDefault();
				this._load();
			});
		}

		if (this.supportBackButton === 1) {
			window.addEventListener('popstate', () => {
				let lastTime, items;

				if (this.supportBackButton === 1) {
					items = this.targetEl.querySelectorAll(this.itemSelector);

					if (this.itemCount.length > 0) {
						lastTime = this.itemCount.pop();
					}

					for (let i = 1; i <= lastTime; i++) {
						this.targetEl.removeChild(items[items.length - i]);
					}
				}
			});
		}
	}
}
